import React, { useEffect, useState } from 'react';
import './research-home.css';
import { useLiveQuotes } from '../desk/useLiveQuotes';
import Sparkline from './Sparkline';
import { go, fetchLatestEdition, fetchFeed, fetchEditionCatalog, editionUrl, formatMoney, formatPct, formatBig, chgClass, timeAgo, COINS, fetchHomeCoins, fetchHomeChart, fetchHomeCalendar, fetchHomeSectors } from './homeData';
import { editorialContent, editionSummary, isOlderEdition, publishedNotes, evidenceRows, publicationTitle } from './editorialData';

function ResearchText({ text, edition }) {
  // Previews link each original citation to the complete edition's evidence.
  return String(text || '').split(/(\[\d+\])/g).map((part, index) => /^\[\d+\]$/.test(part)
    ? <a className="rh-cite" key={index} href={editionUrl(edition)} onClick={go(editionUrl(edition))} aria-label={`Read evidence source ${part.slice(1, -1)} in the edition`}>{part}</a>
    : <React.Fragment key={index}>{part}</React.Fragment>);
}

function Masthead() {
  const [query, setQuery] = useState('');
  const [hint, setHint] = useState('');
  function search(event) {
    event.preventDefault();
    const symbol = query.trim().toUpperCase();
    if (COINS[symbol]) go(`/research/coins/${symbol}`)();
    else setHint('Use a supported symbol such as BTC, ETH, SOL or LINK.');
  }
  return <>
    <header className="rh-mast">
      <a className="rh-brand" href="/research" onClick={go('/research')}>AEON <span className="rh-brand-pill">RESEARCH</span></a>
      <span className="rh-mast-motto">Evidence. Interpretation. Accountability.</span>
      <form className="rh-search" onSubmit={search} role="search"><input aria-label="Find a coin" placeholder="Find a coin · BTC, ETH, SOL" value={query} onChange={event => setQuery(event.target.value)}/><button className="rh-btn rh-btn-lime">Find</button></form>
      {hint && <p className="rh-search-hint" role="status">{hint}</p>}
    </header>
    <nav className="rh-tabs" aria-label="Research sections">
      <a className="rh-tab is-active" href="/research" onClick={go('/research')} aria-current="page">The front page</a>
      <a className="rh-tab" href="#rh-notes">Research notes</a><a className="rh-tab" href="#rh-followups">Following the thesis</a><a className="rh-tab" href="#rh-markets">Market data</a>
      <a className="rh-tab" href="/research/archive" onClick={go('/research/archive')}>Archive</a><a className="rh-tab" href="/research/lab" onClick={go('/research/lab')}>Strategy Lab</a>
    </nav>
  </>;
}

function LeadEdition({ edition, loading, error, retry }) {
  const content = editorialContent(edition);
  return <section className="rh-editorial-lead" aria-labelledby="rh-lead-title">
    <div className="rh-editorial-heading"><span className="rh-kicker">The AEON perspective</span><span className="rh-edition-date">{edition ? `Published ${edition.date} · ${edition.editionType || 'daily'} edition` : 'Research desk'}</span></div>
    <div className="rh-editorial-columns"><div>
      <h1 id="rh-lead-title">{edition ? <a href={editionUrl(edition)} onClick={go(editionUrl(edition))}>{publicationTitle(edition)}</a> : 'Understand the move. Examine the evidence.'}</h1>
      {edition ? <>
        {content.dek && <p className="rh-editorial-dek">{content.dek}</p>}
        <p className="rh-byline">AEON Research · AI-assisted analysis{content.reading_time_minutes ? ` · ${content.reading_time_minutes} min read` : ''}</p>
        {isOlderEdition(edition) && <p className="rh-age-notice">Latest available edition · published {edition.date}. Readings below belong to that edition, not the live market.</p>}
        <a className="rh-read-link" href={editionUrl(edition)} onClick={go(editionUrl(edition))}>Read the analysis <span aria-hidden="true">↗</span></a>
      </> : <div role="status"><p className="rh-editorial-dek">{loading ? 'Loading the latest published research…' : error || 'No edition has been published yet.'}</p>{!loading && <button className="rh-btn rh-btn-ghost" onClick={retry}>Retry</button>}</div>}
    </div><div className="rh-editorial-summary"><h2>In the brief</h2>
      {editionSummary(edition).length ? <ol>{editionSummary(edition).map((line, index) => <li key={index}><span className="rh-summary-number">0{index + 1}</span><p><ResearchText text={line} edition={edition}/></p></li>)}</ol> : <p className="rh-note">Our brief brings together the observations, our interpretation and the next test.</p>}
    </div></div>
  </section>;
}

function ResearchNotes({ edition }) {
  const notes = publishedNotes(edition);
  if (!notes.length) return null;
  return <section id="rh-notes"><div className="rh-section-heading"><h2>Inside the research</h2><span>From the {edition.date} edition</span></div>
    <div className="rh-research-grid">{notes.map((note, index) => <article className="rh-research-note" key={note.key}><div className="rh-note-label"><span>{note.label}</span><span>0{index + 1}</span></div><h3>{note.title}</h3><p><ResearchText text={note.body} edition={edition}/></p><a href={editionUrl(edition)} onClick={go(editionUrl(edition))}>Read analysis & sources →</a></article>)}</div>
  </section>;
}

function Evidence({ edition }) {
  const rows = evidenceRows(edition);
  if (!rows.length) return null;
  return <section className="rh-evidence"><div className="rh-section-heading"><h2>The evidence desk</h2><span>Published snapshot · {edition.date}</span></div><p className="rh-note">Read the measurement and comparison together. Each observation retains its original scope and date.</p><div className="rh-evidence-grid">{rows.map((row, index) => <article key={`${row.metric}-${index}`}><h3>{row.metric}</h3><strong className="rh-evidence-value">{row.reading}</strong><p>{row.context}</p>{row.provenance?.stale && <span className="rh-age-notice">Historical observation · {row.provenance.observationDate || row.provenance.observedAt}</span>}<a href={editionUrl(edition)} onClick={go(editionUrl(edition))}>View evidence{row.sourceIndex ? ` [${row.sourceIndex}]` : ''} →</a></article>)}</div></section>;
}

function Followups({ edition, catalog, archiveError }) {
  const section = editorialContent(edition).sections?.scorecard;
  const calls = section?.items || edition?.recap || [];
  const earlier = catalog.filter(item => (item.editionId || item.date) !== (edition?.editionId || edition?.date)).slice(0, 3);
  return <section id="rh-followups"><div className="rh-section-heading"><h2>Following the thesis</h2><a href="/research/archive" onClick={go('/research/archive')}>Full archive ↗</a></div>
    <div className="rh-followup-intro"><span className="rh-kicker">A view should survive a second look.</span><p>Return to the original argument, its evidence and the conditions that would change it.</p></div>
    {calls.length > 0 && <ul className="rh-scorecards">{calls.slice(0, 4).map((call, index) => <li key={index}><span className="rh-tag">{call.outcome || call.result || 'Under review'}</span><h3>{call.call}</h3><p><ResearchText text={call.note} edition={edition}/></p>{call.edition_id && <a href={editionUrl({editionId: call.edition_id})} onClick={go(editionUrl({editionId: call.edition_id}))}>Original argument →</a>}</li>)}</ul>}
    {!calls.length && <p className="rh-note">No graded follow-ups in this edition. Earlier editions remain available for comparison.</p>}
    <div className="rh-archive-list">{earlier.map(item => <article key={item.editionId || item.date}><time>{item.date}</time><h3><a href={editionUrl(item)} onClick={go(editionUrl(item))}>{publicationTitle(item)}</a></h3><span>{item.editionType || 'Daily'} edition →</span></article>)}</div>
    {archiveError && <p className="rh-note">Recent editions could not be loaded. <a href="/research/archive" onClick={go('/research/archive')}>Open the archive</a>.</p>}
  </section>;
}

function AroundMarket({ feed, error }) {
  return <section className="rh-card rh-around"><div className="rh-kicker">Outside coverage</div><h2>Around the market</h2><p className="rh-note">Reporting from other publishers. Links open the original coverage.</p><ul className="rh-feedlist">{feed.slice(0, 5).map(item => <li key={item.id || item.url}><span className="rh-source-label">{item.publisher || item.source || 'External publisher'} · {timeAgo(item.publishedAt)}</span><a href={item.url} target="_blank" rel="noopener noreferrer">{item.title} ↗</a></li>)}</ul>{!feed.length && <p className="rh-note">{error ? 'Outside coverage is temporarily unavailable.' : 'No external headlines available.'}</p>}<a className="rh-rail-link" href="/research/feed" onClick={go('/research/feed')}>Browse source feed →</a></section>;
}

function EditorialStandard() {
  return <section className="rh-card rh-standard"><div className="rh-kicker">How we work</div><h2>Our argument.<br/>Your evidence.</h2><p>AEON connects market observations into a testable view. We distinguish reported facts from interpretation, keep sources attached, and revisit earlier calls.</p><p>AI-assisted analysis. Missing evidence stays missing. A pending thesis is not a winning call.</p><a href="/research/coverage" onClick={go('/research/coverage')}>Coverage & methodology →</a></section>;
}
/* ---------------- markets pulse ---------------- */
const RANGE_DAYS = { Day: 1, Week: 7, Month: 30, Year: 365, Max: 365 };

function MarketsPulse({ coins, global }) {
  const [range, setRange] = useState("Month");
  const [chart, setChart] = useState(null);
  const [chartErr, setChartErr] = useState(false);
  const bySym = {};
  (coins || []).forEach((c) => { if (c && c.symbol) bySym[c.symbol] = c; });
  const btc = bySym.BTC;
  const eth = bySym.ETH;

  useEffect(() => {
    let dead = false;
    setChart(null);
    setChartErr(false);
    fetchHomeChart("BTC", RANGE_DAYS[range] || 30)
      .then((d) => { if (!dead) setChart(d); })
      .catch(() => { if (!dead) setChartErr(true); });
    return () => { dead = true; };
  }, [range]);

  const tiles = [
    { sym: "BTC/USD", sub: "Bitcoin", price: btc && btc.price != null ? formatMoney(btc.price) : "\u2014", chg: btc ? btc.change_24h_pct ?? null : null },
    { sym: "ETH/USD", sub: "Ethereum", price: eth && eth.price != null ? formatMoney(eth.price) : "\u2014", chg: eth ? eth.change_24h_pct ?? null : null },
    { sym: "TOTAL", sub: "Crypto market cap", price: global && global.total_mcap != null ? formatBig(global.total_mcap) : "\u2014", chg: null },
    { sym: "BTC.D", sub: "Bitcoin dominance", price: global && global.btc_dominance != null ? global.btc_dominance.toFixed(1) + "%" : "\u2014", chg: null },
  ];
  return (
    <section id="rh-markets" aria-label="Markets pulse">
      <h3 className="rh-sect">Markets Pulse</h3>
      <div className="rh-card">
        <div className="rh-tiles">
          {tiles.map((tl) => (
            <div className="rh-tile" key={tl.sym}>
              <div className="rh-sym">{tl.sym}</div><div className="rh-sub">{tl.sub}</div>
              <div className="rh-price">{tl.price}</div>
              <div className={`rh-chg ${chgClass(tl.chg)}`}>{tl.chg != null ? formatPct(tl.chg) : "\u2014"}</div>
            </div>
          ))}
        </div>
        <div className="rh-chartbox" style={chart && chart.points && chart.points.length >= 2 ? { display: "block", padding: 10 } : undefined}>
          {chart && chart.points && chart.points.length >= 2 ? (
            <Sparkline points={chart.points} />
          ) : chartErr ? (
            <>BTC chart unavailable right now.</>
          ) : (
            <>Loading BTC chart…</>
          )}
        </div>
        <div className="rh-range" role="group" aria-label="Chart range">
          {Object.keys(RANGE_DAYS).map((r) => (
            <button key={r} type="button" className={r === range ? "is-on" : ""} onClick={() => setRange(r)}>{r}</button>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- sector map ---------------- */
function SectorMap({ sectors }) {
  const list = sectors || [];
  return (
    <section aria-label="24h change by crypto sector">
      <h3 className="rh-sect">24H Change by Crypto Sector</h3>
      <div className="rh-card">
        {list.length ? (
          <div className="rh-sectors">
            {list.map((s) => (
              <div className="rh-sector s-big" key={s.sector}>
                <b>{s.sector}</b>
                <span className={`rh-val ${chgClass(s.change_24h_pct)}`}>
                  {s.change_24h_pct != null ? formatPct(s.change_24h_pct) : "\u2014%"}
                </span><br /><span className="rh-top">{(s.coins || []).join(" \u00b7 ")}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="rh-empty">Sector data unavailable right now.</div>
        )}
        <p className="rh-note" style={{ marginTop: 10 }}>Color = 24h change · CoinGecko via AEON.</p>
      </div>
    </section>
  );
}


/* ---------------- coin spotlight ---------------- */
function CoinSpotlight({ coins }) {
  const rows = (coins || []).map((c) => ({
    sym: c.symbol,
    name: (COINS[c.symbol] || {}).name || c.symbol,
    price: c.price != null ? formatMoney(c.price) : "\u2014",
    chg24: c.change_24h_pct ?? null,
    chg7: c.change_7d_pct ?? null,
    mcap: c.market_cap != null ? formatBig(c.market_cap) : "\u2014",
  }));
  return (
    <section aria-label="Coin spotlight">
      <h3 className="rh-sect">Coin Spotlight</h3>
      <div className="rh-card" style={{ padding: 6 }}>
        {rows.length ? (
          <table className="rh-table">
            <thead><tr><th>Coin</th><th>Price</th><th>24h</th><th>7d</th><th>Mkt cap</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.sym}>
                  <td><a className="rh-coinlink" href={`/research/coins/${r.sym}`} onClick={go(`/research/coins/${r.sym}`)}>{r.sym}</a><div style={{ fontSize: 11, color: "var(--rh-faint)" }}>{r.name}</div></td>
                  <td className="rh-num">{r.price}</td>
                  <td className={`rh-num ${chgClass(r.chg24)}`}>{r.chg24 != null ? formatPct(r.chg24) : "\u2014"}</td>
                  <td className={`rh-num ${chgClass(r.chg7)}`}>{r.chg7 != null ? formatPct(r.chg7) : "\u2014"}</td>
                  <td className="rh-num">{r.mcap}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="rh-empty">Coin data unavailable right now.</div>
        )}
      </div>
    </section>
  );
}


function CatalystCalendar({ events }) {
  const list = (events || []).slice(0, 6);
  return (
    <div className="rh-card" aria-label="Catalyst calendar">
      <h3>Catalyst Calendar</h3>
      {list.length ? (
        <ul className="rh-newslist">
          {list.map((e, i) => (
            <li key={`${e.date}-${i}`}>
              <div>
                <h4 style={{ fontSize: 13.5 }}>{e.name}</h4>
                <div className="rh-newsmeta">
                  {e.date}{e.time_ct ? ` \u00b7 ${e.time_ct} CT` : ""}{e.importance ? ` \u00b7 ${e.importance}` : ""}
                </div>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div className="rh-empty">No upcoming catalysts in the calendar.</div>
      )}
    </div>
  );
}


export default function ResearchHome() {
  const [data, setData] = useState({edition: null, feed: [], catalog: [], coins: [], sectors: [], events: []});
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [retry, setRetry] = useState(0);
  const live = useLiveQuotes(true);
  useEffect(() => {
    let dead = false;
    async function load() {
      const tasks = {edition: fetchLatestEdition, feed: () => fetchFeed(5), catalog: fetchEditionCatalog, coins: fetchHomeCoins, sectors: fetchHomeSectors, events: fetchHomeCalendar};
      await Promise.allSettled(Object.entries(tasks).map(async ([key, task]) => {
        try {
          const value = await task();
          if (dead) return;
          setData(previous => ({...previous, ...(key === 'coins'
            ? {coins: value.coins || [], global: value.global || null}
            : key === 'sectors' ? {sectors: value.sectors || []} : {[key]: value})}));
          setErrors(previous => ({...previous, [key]: false}));
        } catch {
          if (!dead) setErrors(previous => ({...previous, [key]: true}));
        } finally {
          if (!dead && key === 'edition') setLoading(false);
        }
      }));
    }
    setLoading(true); load();
    const timer = setInterval(load, 5 * 60 * 1000);
    return () => {dead = true; clearInterval(timer);};
  }, [retry]);
  return <div className="rh rh-publication">
    <Masthead/>
    {live?.quotes?.length > 0 && <div className="rh-ticker"><div className="rh-ticker-track"><span className="rh-kicker">{live.mode === 'live' ? 'Live prices' : 'Price snapshot'}</span>{live.quotes.map(quote => <a className="rh-tick" key={quote.symbol} href={`/research/coins/${quote.symbol}`} onClick={go(`/research/coins/${quote.symbol}`)}><b>{quote.symbol}</b>{formatMoney(quote.price) || '—'}</a>)}</div></div>}
    <main className="rh-wrap">
      <LeadEdition edition={data.edition} loading={loading} error={errors.edition ? 'The latest edition could not be loaded.' : ''} retry={() => setRetry(value => value + 1)}/>
      {errors.edition && data.edition && <p className="rh-age-notice" role="status">Refresh failed. Showing the last loaded edition.</p>}
      <ResearchNotes edition={data.edition}/>
      <div className="rh-main rh-publication-main"><div className="rh-col">
        <Evidence edition={data.edition}/><Followups edition={data.edition} catalog={data.catalog} archiveError={errors.catalog}/>
        <MarketsPulse coins={data.coins} global={data.global}/><SectorMap sectors={data.sectors}/><CoinSpotlight coins={data.coins}/>
      </div><aside className="rh-rail"><EditorialStandard/><CatalystCalendar events={data.events}/><AroundMarket feed={data.feed} error={errors.feed}/><div className="rh-subscribe"><h3>The AEON Brief</h3><p>The full market argument: evidence, scenarios and what would change our view.</p><a className="rh-read-link" href="/research/brief" onClick={go('/research/brief')}>Read the brief →</a></div></aside></div>
    </main><footer className="rh-footer rh-wrap"><span>AEON Research · Evidence before conviction.</span><span>AI-assisted market commentary · Not financial advice.</span></footer>
  </div>;
}
